import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { isNil } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Loader } from '@/components/loaders/Loader'
import NoData from '@/components/NoData'
import { TreeTable } from '@/components/tables/TreeTable/TreeTable'
import { toast } from '@/components/toasts/toast.helpers'
import {
  DRIVE_NOT_FOUND,
  NOT_AWAITING_REVIEW,
  NOT_FOUND,
  NOT_SYNCED,
  fetchConnectionItems,
  fetchConnectionReview,
  submitConnectionReview,
} from '@/reducers/organizations/documentSourceConnection.actions'
import { FILE_COLUMNS, cellsOf } from '@/views/Organizations/components/ManageFilesModal'
import {
  EMPTY_CACHE,
  appendPage,
  completeChildrenOf,
  markListing,
  recordChange,
  rootKey,
  toggleTarget,
} from '@/views/Organizations/components/ManageFilesModal.helpers'
import {
  REVIEW_STATE,
  canStartSync,
  fetchEveryPage,
  isListingComplete,
  isNestedRoot,
  reviewPayload,
  reviewRootsOf,
  reviewRowsOf,
  uncheckedTopLevelOf,
} from '@/views/Organizations/components/ReviewFilesModal.helpers'
import { ScanningModal } from '@/views/Organizations/components/ScanningModal'

const TITLE = 'Review files'
const CANCEL_LABEL = 'Cancel'
const START_SYNC_LABEL = 'Start sync'
const STARTED_COPY = 'Your files will start syncing soon.'
const REVIEWED_COPY = 'This connection has already been reviewed.'
const CHANGED_COPY = 'This connection changed. Close and reopen.'
const CHANGED_CLOSED_COPY = 'This connection changed. Open the review again.'
const GONE_COPY = 'This connection no longer exists.'
const LOAD_FAILED_COPY = "Couldn't load this connection's files. Close and reopen to retry."
const SUBMIT_FAILED_COPY = "Couldn't start the sync. Try again."

// Each of these means the connection moved on since the modal opened; a retry can't succeed.
// Read at call time: the constants can still be unset while the entry import loop evaluates.
const isChanged = (error) => [NOT_AWAITING_REVIEW, NOT_SYNCED, DRIVE_NOT_FOUND].includes(error.kind)

const failureCopy = (error, fallback) =>
  isChanged(error) ? CHANGED_COPY : error.message ?? fallback

// The modal is gone, so "close and reopen" has nothing to close.
const closedFailureCopy = (error) =>
  isChanged(error) ? CHANGED_CLOSED_COPY : failureCopy(error, SUBMIT_FAILED_COPY)

/**
 * Review files: before a new connection's first sync, its creator ticks what syncs in today's
 * Manage files tree. Every top-level item starts unticked; Start sync sends everything left
 * unticked as a block in one request, which starts the first sync.
 *
 * @param {object} props
 * @param {number} props.connectionId the document source connection under review
 * @param {Function} props.onClose closes the modal, with no argument; a Start sync still running
 *   then reports its outcome as a toast
 * @param {Function} [props.onSubmitted] called, with no argument, when a Start sync that outlived
 *   the modal succeeds, so the host can refresh what the earlier close skipped; never called when
 *   the success closes the modal through onClose
 */
export const ReviewFilesModal = ({ connectionId, onClose, onSubmitted }) => {
  const dispatch = useDispatch()
  const [review, setReview] = useState(null)
  const [reviewError, setReviewError] = useState(null)
  const [cache, setCache] = useState(EMPTY_CACHE)
  const [changes, setChanges] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const mounted = useRef(true)

  const roots = isNil(review) ? [] : reviewRootsOf(review.roots)
  const nestedRoots = isNil(review) ? [] : review.nested_root_ids
  const awaitingReview = review?.review_state === REVIEW_STATE.AWAITING

  useEffect(
    () => () => {
      mounted.current = false
    },
    []
  )

  const loadTopLevel = async (root, nestedRootIds) => {
    const parentKey = rootKey(root)
    setCache((current) => markListing(current, parentKey, { loading: true }))
    const { data, error } = await fetchEveryPage((cursor) =>
      dispatch(
        fetchConnectionItems({ connectionId, driveId: root.driveId, parentId: root.rootId, cursor })
      )
    )
    if (!mounted.current) {
      return
    }

    const target = {
      parentKey,
      driveId: root.driveId,
      ancestorIds: isNil(root.rootId) ? [] : [root.rootId],
      replace: true,
    }
    if (!isNil(error)) {
      setCache((current) => markListing(current, parentKey, { error }))
      return
    }
    // Unticked before listed, so no render shows a complete listing with nothing unticked.
    setChanges((current) => [...current, ...uncheckedTopLevelOf(root, data.items, nestedRootIds)])
    setCache((current) => appendPage(current, target, data))
  }

  useEffect(() => {
    const loadReview = async () => {
      const { data, error } = await dispatch(fetchConnectionReview({ connectionId }))
      if (!mounted.current) {
        return
      }
      if (!isNil(error)) {
        setReviewError(error)
        return
      }

      setReview(data)
      if (data.review_state === REVIEW_STATE.AWAITING) {
        reviewRootsOf(data.roots).forEach((root) => loadTopLevel(root, data.nested_root_ids))
      }
    }
    loadReview()
  }, [])

  // ManageFilesModal's loadChildren minus its stale-cursor restart: a review's manifest is fixed.
  const loadChildren = async (loadTarget, cursor = null) => {
    const { parentKey, driveId, parentId, ancestorIds } = loadTarget
    const listing = cache.listings[parentKey]
    // Expanding fetches a first page only when there is none, or the last attempt failed.
    if (listing?.loading || (isNil(cursor) && !isNil(listing) && isNil(listing.error))) {
      return
    }

    setCache((current) => markListing(current, parentKey, { loading: true }))
    const { data, error } = await dispatch(
      fetchConnectionItems({ connectionId, driveId, parentId, cursor })
    )
    if (!mounted.current) {
      return
    }

    if (isNil(error)) {
      const replace = isNil(cursor)
      setCache((current) => appendPage(current, { parentKey, driveId, ancestorIds, replace }, data))
    } else {
      setCache((current) => markListing(current, parentKey, { error }))
    }
  }

  // As ManageFilesModal's toggle.
  const toggle = (item) => {
    const childrenOf = completeChildrenOf(cache)
    setChanges((current) =>
      recordChange(current, item, toggleTarget(item, current, childrenOf), childrenOf)
    )
  }

  // The POST commits server-side, so its outcome is reported even after the modal is gone.
  const startSync = async () => {
    setSubmitError(null)
    setSubmitting(true)
    const { error } = await dispatch(
      submitConnectionReview({ connectionId, blocked: reviewPayload(changes) })
    )
    if (isNil(error)) {
      toast.success(STARTED_COPY)
      if (mounted.current) {
        onClose()
      } else if (!isNil(onSubmitted)) {
        onSubmitted()
      }
      return
    }
    if (!mounted.current) {
      toast.error(closedFailureCopy(error))
      return
    }

    setSubmitting(false)
    setSubmitError(error)
  }

  const toTreeRows = (models) =>
    models.map((model) => ({
      ...model,
      ...cellsOf(model),
      // A nested scope root can never be blocked (the server refuses it), so it stays included.
      checkboxDisabled:
        submitting ||
        model.checkboxDisabled ||
        (!isNil(model.item) && isNestedRoot(nestedRoots, model.item)),
      childRows: toTreeRows(model.childRows),
      onShowMore: isNil(model.nextCursor)
        ? undefined
        : () => loadChildren(model.loadTarget, model.nextCursor),
    }))

  const renderListing = (root) => {
    const listing = cache.listings[rootKey(root)]
    if (!isNil(listing?.error)) {
      return <ErrorMessage content={failureCopy(listing.error, LOAD_FAILED_COPY)} />
    }
    if (!isListingComplete(cache, root)) {
      return <Loader centered />
    }

    return (
      <TreeTable
        rowKey="key"
        columns={FILE_COLUMNS}
        rows={toTreeRows(reviewRowsOf({ root, cache, changes }))}
        checkboxLabel={(row) => `Include ${row.label}`}
        onRowToggle={(row) => toggle(row.item)}
        onExpand={(row) => {
          if (!isNil(row.loadTarget)) {
            loadChildren(row.loadTarget)
          }
        }}
      />
    )
  }

  const renderRoot = (root) => (
    <section key={rootKey(root)} className="mb" data-testid={`review-root-${rootKey(root)}`}>
      <div className="mbs">
        <h4 className="no-margin">{root.label}</h4>
      </div>
      {renderListing(root)}
    </section>
  )

  const renderBody = () => {
    if (reviewError?.kind === NOT_FOUND) {
      return <NoData hideHeader text={GONE_COPY} />
    }
    if (!isNil(reviewError)) {
      return <ErrorMessage content={reviewError.message ?? LOAD_FAILED_COPY} />
    }
    if (isNil(review)) {
      return <Loader centered />
    }
    if (isNil(review.review_state)) {
      return <NoData hideHeader text={REVIEWED_COPY} />
    }

    return roots.map(renderRoot)
  }

  if (review?.review_state === REVIEW_STATE.SCANNING) {
    return <ScanningModal onClose={onClose} />
  }

  return (
    <BasicModal
      show
      size="large"
      scrolling
      title={TITLE}
      data-testid="review-files-modal"
      onClose={onClose}
      footer={
        <>
          <Button className="ml-auto" onClick={onClose}>
            {CANCEL_LABEL}
          </Button>
          {awaitingReview && (
            <Button
              brand
              loading={submitting}
              disabled={!canStartSync({ roots, cache, changes, nestedRoots })}
              onClick={startSync}
            >
              {START_SYNC_LABEL}
            </Button>
          )}
        </>
      }
    >
      {/* File names can carry PHI, in text and attributes; keep them out of session replays. */}
      <div className="fs-exclude" data-testid="review-files-body">
        {!isNil(review?.label) && <p className="field-help">{review.label}</p>}
        {!isNil(submitError) && (
          <ErrorMessage className="mb" content={failureCopy(submitError, SUBMIT_FAILED_COPY)} />
        )}
        {renderBody()}
      </div>
    </BasicModal>
  )
}
